import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { getPost, BLOG_POSTS } from "@/lib/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  if (!post) {
    return (
      <div className="pt-28 md:pt-32 pb-24 min-h-screen text-center" data-testid="blog-post-notfound">
        <div className="serif text-4xl text-[#0d1b3e]">Article not found</div>
        <Link to="/blog" className="btn-sharp btn-sharp-gold mt-8 inline-flex">Back to blog</Link>
      </div>
    );
  }

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-blog-post">
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#4a5570] hover:text-[#0d1b3e] transition-colors">
          <ArrowLeft size={14} /> All articles
        </Link>

        <div className="mt-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#b8892b]" data-testid="blog-post-meta">
            {post.tags.join(" · ")} · {post.date} · {post.read}
          </div>
          <h1 className="serif text-4xl md:text-6xl leading-[1] tracking-tight text-[#0d1b3e] mt-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-[#4a5570] leading-relaxed">{post.excerpt}</p>
          <div className="mt-6 text-sm text-[#4a5570]">By <span className="text-[#0d1b3e] font-medium">{post.author}</span></div>
        </div>

        <div className="mt-12 aspect-[16/9] overflow-hidden bg-[#0d1b3e]" data-testid="blog-post-hero">
          <img src={post.hero} alt={post.title} className="w-full h-full object-cover" loading="eager" />
        </div>

        <article className="mt-14 space-y-10 max-w-[720px] mx-auto" data-testid="blog-post-body">
          {post.body.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
            >
              <h2 className="serif text-2xl md:text-3xl text-[#0d1b3e] leading-tight">{s.h}</h2>
              <p className="mt-3 text-[17px] leading-[1.7] text-[#0d1b3e]/85">{s.p}</p>
            </motion.section>
          ))}
        </article>

        <div className="mt-16 bg-[#0d1b3e] text-white p-8 md:p-10 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="eyebrow !text-[#b8892b]">Ready?</div>
            <div className="serif text-3xl mt-2">Turn this guide into a plan.</div>
          </div>
          <div className="md:justify-self-end flex flex-wrap gap-3">
            <Link to="/advisor" className="btn-sharp btn-sharp-gold" data-testid="blog-cta-advisor">Get my free study plan</Link>
            <Link to="/calculator" className="btn-sharp !bg-transparent !text-white !border-white/40 hover:!bg-white hover:!text-[#0d1b3e]" data-testid="blog-cta-calculator">Estimate my costs</Link>
          </div>
        </div>

        {others.length > 0 && (
          <div className="mt-20">
            <div className="eyebrow mb-6">Keep reading</div>
            <div className="grid md:grid-cols-2 gap-6">
              {others.map((o) => (
                <Link key={o.slug} to={`/blog/${o.slug}`} data-testid={`blog-related-${o.slug}`}
                  className="group bg-white border border-[#0d1b3e]/10 p-6 hover:border-[#b8892b] transition-colors">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#b8892b]">{o.tags.join(" · ")}</div>
                  <div className="serif text-xl text-[#0d1b3e] mt-2 leading-tight group-hover:text-[#b8892b] transition-colors">{o.title}</div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
